import { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { useHistory, Link, useLocation, Redirect } from "react-router-dom";
import ReactGA from "react-ga";
import classes from "../TrucksAvailable/TrucksAvailable.module.css";
import classes1 from "../Indent/IndentMain.module.css";
import { Activity, currentRequestTimeStamp, isMobile } from "../../Constants";
import Unauthorized from "../Unauthorized";
import { Row } from "../Shared/FlexDiv/FlexDiv";
import IndentRoute from "./IndentRoute";
import { UserApiContext } from "../UserDetailsContext/UserDetailsContext";
import { useIndentTracking } from "./hooks/useIndentTracking";
import { toast } from 'react-toastify';
import useViewOnlyAccess from "../../hooks/useViewOnlyAccess";

const IndentMain = () => {
  const history = useHistory();
  const location = useLocation();

  // Contexts
  const { userDetails } = useContext(UserApiContext);

  const { trackIndentEvent } = useIndentTracking();

  const { isRestricted: rt, hasModuleAccess: trackAccess, noAccess: trackNoAccess, dashboardAccess: trackDashboardAccess, loading: trackLoading } = useViewOnlyAccess('indent', 'lorri_indent_tracking', true);
  const { isRestricted: rr, hasModuleAccess: reqAccess, noAccess: reqNoAccess, dashboardAccess: reqDashboardAccess, loading: reqLoading } = useViewOnlyAccess('indent', 'lorri_indent_request', true);
  const isRestricted = rt || rr;
  const dashboardLocked = !reqDashboardAccess && !trackDashboardAccess;

  const isDashboardAllowedByPolicy =
    userDetails?.policy?.indents?.allowed_dashboard === "yes";

  // States
  const [policy, setPolicy] = useState(null);
  const [userContent, setUserContent] = useState(null);

  useEffect(() => {
    const headerLink = document.querySelector('.nav-link[data-section="indent"]');
    if (headerLink) {
      document
        .querySelectorAll(".nav-link")
        .forEach((el) => el.classList.remove("active"));
      headerLink.classList.add("active");
    }

    return () => {
      const headerLink = document.querySelector('.nav-link[data-section="indent"]');
      if (headerLink) headerLink.classList.remove("active");
    };
  }, []);

  useEffect(() => {
    ReactGA.pageview("Indent");

    Activity([
      {
        screen: "Indents",
        module: "Indents",
        user_activity: "Indent page visited",
        timestamp: currentRequestTimeStamp(),
        "userEmail": userDetails?.email, "userName": userDetails?.user_name, companyname: userDetails.company_name ? userDetails.company_name : userDetails.email ? userDetails.email.split("@")[1].split(".")[0] : ""
      },
    ]);

    trackIndentEvent("indent", "user has visited indent module");

    const userPolicy = userDetails?.policy;
    setPolicy(userPolicy);

    if (userPolicy?.indents?.allowed !== "yes") {
      setUserContent(<Unauthorized />);
    }
  }, [userDetails]);

  // Handle unauthorized access separately before rendering
  const accessDenied = !trackLoading && !reqLoading && trackAccess === false && reqAccess === false;

  // CALLBACKS
  const handleNavigation = useCallback(
    (path, screen, activity) => {
      Activity([
        {
          screen: "Indents",
          module: screen,
          user_activity: activity,
          timestamp: currentRequestTimeStamp(),
          "userEmail": userDetails?.email, "userName": userDetails?.user_name, companyname: userDetails.company_name ? userDetails.company_name : userDetails.email ? userDetails.email.split("@")[1].split(".")[0] : ""
        },
      ]);

      trackIndentEvent(screen, activity);

      history.push({ pathname: path });
    },
    [history]
  );

  const allTabs = useMemo(
    () => [
      {
        path: "/indents/requestdashboard",
        label: "Indent Dashboard",
        screen: "indent dashboard",
        activity: "user has clicked on indent dashboard tab",
        disabled: dashboardLocked,
        policyHidden: !isDashboardAllowedByPolicy,
      },
      {
        path: "/indentrequest",
        label: "Indent Request",
        screen: "indent request",
        activity: "user has clicked on indent request tab",
        disabled: reqNoAccess
      },
      {
        path: "/indenttracking",
        label: "Indent Tracking",
        screen: "indent tracking",
        activity: "user has clicked on indent tracking tab",
        disabled: trackNoAccess
      },
    ],
    [reqNoAccess, trackNoAccess, dashboardLocked, isDashboardAllowedByPolicy]
  );

  const tabs = useMemo(
    () => allTabs.filter((t) => !t.policyHidden),
    [allTabs]
  );

  if (accessDenied) {
    return (
      <Unauthorized
        message="You dont have access for this module"
        showPremiumButton={false}
      />
    );
  }

  if (policy?.indents?.allowed !== "yes") return userContent;

  const activePath = location.pathname;
  const isRequestView = activePath.includes("request");
  const activeRestricted = isRequestView ? rr : rt;
  const activeModuleLabel = isRequestView ? "Indent Request" : "Indent Tracking";

  // Safe redirect logic
  let redirectPath = null;
  if (!trackLoading && !reqLoading) {
    if (!isDashboardAllowedByPolicy && location.pathname === "/indents/requestdashboard") {
      redirectPath = !reqNoAccess ? "/indentrequest" : (!trackNoAccess ? "/indenttracking" : null);
    } else if (dashboardLocked && location.pathname === "/indents/requestdashboard") {
      redirectPath = !reqNoAccess ? "/indentrequest" : (!trackNoAccess ? "/indenttracking" : null);
    } else if (reqNoAccess && location.pathname === "/indentrequest") {
      redirectPath = !trackNoAccess ? "/indenttracking" : "/indents/requestdashboard";
    } else if (trackNoAccess && location.pathname === "/indenttracking") {
      redirectPath = !reqNoAccess ? "/indentrequest" : "/indents/requestdashboard";
    }
  }

  // Prevent redirect loops if redirectPath is same as current path or null
  if (redirectPath && redirectPath !== location.pathname) {
    return <Redirect to={redirectPath} />;
  }

  const renderTabs = () => (
    <nav className={classes1["headerNav"]} role="tablist" id="myTab">
      {tabs.map(({ path, label, screen, activity, disabled }) => {
        const isDashboardTab = path === "/indents/requestdashboard";
        const isActive = isDashboardTab
          ? location.pathname.startsWith("/indents/")
          : location.pathname === path;
        return (
          <Link
            key={path}
            role="tab"
            aria-selected={isActive}
            className={`${classes1["headerNavLink"]} ${isActive ? classes1["headerNavLinkActive"] : ""} ${disabled ? classes1["restricted-link"] : ""}`}
            onClick={() => !disabled && handleNavigation(path, screen, activity)}
            to={!disabled ? { pathname: path } : "#"}
          >
            <span className={classes1["tabLabelWrap"]}>
              {label}
              {isDashboardTab && (
                <span className={classes1["newBadge"]}>NEW</span>
              )}
            </span>
            {disabled && <i className="fa fa-lock ms-2" />}
          </Link>
        );
      })}
    </nav>
  );

  return (
    <div>
      <div className={classes1["indentHeaderBar"]}>
        {!isMobile() && renderTabs()}
        {activeRestricted && (
          <span className={classes1["viewOnlyBadge"]}>
            <i className="fa fa-eye" style={{ marginRight: "6px", fontSize: "13px" }} />
            View Only
          </span>
        )}
      </div>

      {!isMobile() ? (
        <div style={{ width: "90%", margin: "auto", position: "relative" }}>
          <IndentRoute />
        </div>
      ) : (
        <div>
          <div
            style={{
              display: 'flex',
              width: '100%',
              borderBottom: '2px solid #e0e0e0',
              backgroundColor: '#fff',
              position: 'sticky',
              top: 0,
              zIndex: 20,
            }}
          >
            {tabs.map(({ label, path, screen, activity, disabled }, index) => {
              const isActive = window.location.pathname === path;
              return (
                <button
                  key={path}
                  type="button"
                  disabled={disabled}
                  style={{
                    flex: 1,
                    padding: '10px 4px',
                    fontSize: '13px',
                    fontWeight: isActive ? '700' : '500',
                    color: isActive ? '#393186' : '#666',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: isActive ? '3px solid #393186' : '3px solid transparent',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.5 : 1,
                    transition: 'all 0.2s',
                    marginBottom: '-2px',
                  }}
                  onClick={() => !disabled && handleNavigation(path, screen, activity)}
                >
                  {label} {disabled && <i className="fa fa-lock" style={{ fontSize: '10px' }} />}
                </button>
              );
            })}
          </div>
          <IndentRoute />
        </div>
      )}
    </div>
  );
};

export default IndentMain;
